import { useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Main tab routes — switching between these should fade, not slide
const TAB_PATHS = new Set(['/', '/stats', '/accounts', '/calendar']);

function getPathDepth(pathname) {
  return pathname === '/' ? 0 : pathname.replace(/\/$/, '').split('/').length - 1;
}

/**
 * Detects navigation direction based on route depth changes.
 * Returns 'forward' | 'back' | 'fade' for the current transition.
 */
export default function useNavigationDirection() {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const direction = useMemo(() => {
    const prev = prevPathRef.current;
    const curr = location.pathname;

    // Update ref for next comparison
    prevPathRef.current = curr;

    // Same page — no animation
    if (prev === curr) return 'fade';

    // Tab-to-tab switches get fade
    if (TAB_PATHS.has(prev) && TAB_PATHS.has(curr)) return 'fade';

    const prevDepth = getPathDepth(prev);
    const currDepth = getPathDepth(curr);

    if (currDepth > prevDepth) return 'forward';
    if (currDepth < prevDepth) return 'back';

    // Same depth but different path — fade (lateral navigation)
    return 'fade';
  }, [location.pathname]);

  return direction;
}
